import { getSportsMatches, getSportsTables, getSportsTicker, normalizeTeaserTypes } from './article';
import { getAuthors } from './author'
import { AppTheme, ContentType } from './enums';
import { getImageEntries, getImageEntry, getImageUrlFormat2_3_600 } from './images';
import { AmazonAffiliateBoxTeaserEntry, FaqTeaserEntry, RessortEntry, TeaserEntry, RessortModuleEntry, AudioTeaserEntry, ImageEntry, LinkObject, FreeTextTeaserEntry, AdvertTeaserEntry, CollectiveBoxTeaserEntry, CollectiveSwipeEntry, VideoTeaserEntry, ImageTeaserEntry, QualifioTeaserEntry, InfoBoxTeaserEntry, LinkBoxTeaserEntry, LivetickerTeaserEntry, DailyMotionVideoTeaserEntry, ProviderTeaserEntry, SearchModuleOption, KickerMatchdayTeaserEntry, CatchlineTeaserEntry, RessortCategoryEntry, RessortCategoryMainEntry, AdsEntry } from './interfaces';
import { getEnclosureLink, getExternalLink, getLinkObjectFromTeaser } from './link';
import { getPageInformation } from './pageInformation';
import { getContentType } from './types';

export function getTeaserEntry(entry: any) : TeaserEntry | AudioTeaserEntry | AdvertTeaserEntry | CollectiveSwipeEntry | KickerMatchdayTeaserEntry | AmazonAffiliateBoxTeaserEntry {
    let type = getContentType(entry)

  let images = type != ContentType.KickerMatchday ? getImageEntries(entry) : []
  let imageUrl: string | null = null

  if(type == ContentType.CollectiveSwipe) {
    if(!imageUrl) {
      imageUrl = (images && images.length > 0) ? (getImageUrlFormat2_3_600(images[0]) || null) : null
    }

    
    if(!imageUrl) {
      imageUrl = getEnclosureLink(entry)
    }

    let linkObject: LinkObject | undefined = undefined;
    if(entry?.linkList?.length > 0) {
      linkObject = getLinkObjectFromTeaser(entry?.linkList[0])
    }
    let collectiveSwipeEntry: CollectiveSwipeEntry = {
      linkButtonText: entry.buttonText ?? null,
      linkButtonUrl: (linkObject?.internalLink || linkObject?.externalLink) ?? "",
      imageUrl: imageUrl ?? ""
    }
    return collectiveSwipeEntry
  }
  
  let teaserEntry: TeaserEntry = {
    id: entry.id,
    type: type,
    creationDate: entry.creationDate ?? null,
    date: entry.creationDate ?  Date.parse(entry.creationDate) : Date.now(),
    title: entry.title ?? null,
    ressort: entry.ressort ?? null,
    topline: entry.topline ?? null,
    subline: entry.subline ?? null,
    body: entry.body ?? null,
    lead: entry.lead ?? null,
    modulePatch: entry.modulePatch ?? null,
    links: entry.links ?? [],
    images: images,
    paid: entry.paid === true,
    authors: getAuthors(entry),
    featured: false,
    teaserTypes: normalizeTeaserTypes(entry.teaserTypes ?? []),
    adIdentifier: entry.adIdentifier == true,
    hidePlusIcon: entry.hidePlusIcon == true,
    breakingNewsIdentifier: entry.breakingNewsIdentifier == true,
    sliderView: entry.sliderView === true
  }

  if(type == ContentType.AmazonAffiliateBox) {
    let products = entry.products ? entry.products.map((product) => {
      return {
        links: product.links ?? [],
        images: product.images ?? [],
        originalPrice: product.originalPrice ?? null,
        price: product.price ?? null,
        savingsPercentage: product.savingsPercentage ?? null,
        savings: product.savings ?? null,
        partner: product.partner ?? null,
        title: product.title ?? null
      }
    }) : []
    let amazonAffiliateBoxEntry: AmazonAffiliateBoxTeaserEntry = {
      ...teaserEntry,
      products: products,
      teaserImage: entry.teaserImage == true,
      originalType: entry.type ?? null
    }
    return amazonAffiliateBoxEntry
  }

  if(type == ContentType.Faq) {
    let faqEntry: FaqTeaserEntry = {
      ...teaserEntry,
      faqs: Array.isArray(entry.faqs) ? entry.faqs.map((faq) => {
        return {
          question: faq.question ?? "",
          answer: faq.answer ?? ""
        }
      }) : []
    }
    return faqEntry
  }

  if(type == ContentType.Image) {
    let imageEntry: ImageTeaserEntry = {
      ...teaserEntry,
      image: getImageEntry(entry) as ImageEntry
    }
    return imageEntry
  }

  if(type == ContentType.Advert) {
    let adEntry: AdsEntry | null = null
    if(entry.ads && entry.ads.length > 0) {
      entry.ads.forEach(entry => {
        if(entry.className.indexOf("Mobile_Pos") >= 0) {
          adEntry = entry
        }
      })
    }
    if(adEntry) {
      return {
        ...teaserEntry,
        adType: entry.adType ?? null,
        position: entry.mobilePosition ?? entry.position ?? 0,
        ads: adEntry,
      }
    }
  }

  if(type == ContentType.Podcast) {
    let audioEntry: AudioTeaserEntry = {
      ...teaserEntry,
      audioUrl: getExternalLink(entry),
      linkButtonText: entry.buttonText ?? null,
      linkButtonUrl: getExternalLink(entry)
    }
    return audioEntry
  }

  if(type == ContentType.Video || type == ContentType.GlomexVideo) {
    let videoEntry: VideoTeaserEntry = {
      ...teaserEntry,
      providerId: entry.providerId ?? null,
      integrationId: entry.integrationId ?? null
    }
    return videoEntry
  }

  if(type == ContentType.TwentyThreeDegrees) {
    let providerTeaserEntry: ProviderTeaserEntry = {
      ...teaserEntry,
      providerId: entry.providerId ?? null
    }
    return providerTeaserEntry
  }
  
  if(type == ContentType.DailyMotionVideo) {
    let videoEntry: DailyMotionVideoTeaserEntry = {
      ...teaserEntry,
      providerId: entry.providerId ?? null
    }
    return videoEntry
  }

  if(type == ContentType.QualifioBox) {
    let qualifioEntry: QualifioTeaserEntry = {
      ...teaserEntry,
      div: entry.codeSnippet.web ?? null,
      providerId: entry.providerId ?? null
    }
    return qualifioEntry
  }

  if(type == ContentType.Liveticker) {
    let tickerEntry: LivetickerTeaserEntry = {
      ...teaserEntry,
      providerId: entry.providerId ?? null,
      themeId: entry.themeId ?? null,
      historicalTicker: entry.historicalTicker == true
    }
    return tickerEntry
  }

  if(type == ContentType.KickerMatchday) {
    const liveTicker = entry.kickerLiveticker
    
    let kickerMatchdayEntry: KickerMatchdayTeaserEntry = {
      ...teaserEntry,
      sportsTicker: liveTicker ? getSportsTicker(liveTicker) : null,
      sportsMatches: liveTicker.matchDay ? getSportsMatches(liveTicker.matchDay): null,
      sportsTables: liveTicker.tables ? getSportsTables(liveTicker.tables): null,
    }
    return kickerMatchdayEntry
  }

  if(type == ContentType.FreeText) {
    let freeTextEntry: FreeTextTeaserEntry = {
      ...teaserEntry,
      linkButtonText: entry.buttonText ?? null,
      linkButtonUrl: getExternalLink(entry)
    }
    return freeTextEntry
  }

  if(type == ContentType.CollectiveBox) {
    let collectiveBoxEntry: CollectiveBoxTeaserEntry = {
      ...teaserEntry,
      teasers: getTeaserSwipeEntries(entry)?.filter((x) => x.linkButtonUrl)
    }
    return collectiveBoxEntry
  } 

  if(type == ContentType.InfoBox) {
    let infoBoxEntry: InfoBoxTeaserEntry = {
      ...teaserEntry,
      showHrLines: entry.showHrLines == true
    }
    return infoBoxEntry
  }

  if(type == ContentType.Linkbox) {
    let linkBoxEntry: LinkBoxTeaserEntry = {
      ...teaserEntry,
      linkList: entry.linkList ? entry.linkList.map((linkEntry) =>{ 
        return getTeaserEntry(linkEntry)}).filter(e => e != null) : []
    }
    return linkBoxEntry
  }

  if(type == ContentType.Catchline) {
    let catchlineEntry: CatchlineTeaserEntry = {
      ...teaserEntry,
      teasers: getTeaserEntries(entry)
    }
    return catchlineEntry
  }

  // if(type == ContentType.Unknown) {
  //   return null
  // }

  return teaserEntry
}

export function getTeaserSwipeEntries(entry: any) : CollectiveSwipeEntry[]  {
  let teasers: CollectiveSwipeEntry[] = []
  if(entry && entry.teasers && entry.teasers.length > 0) {
    teasers = entry.teasers.map((e) => {
      return getTeaserEntry(e)
    }).filter(e => e != null)
  }

  return teasers
}

export function getTeaserEntries(entry: any) : TeaserEntry[]  {
  let teasers: TeaserEntry[] = []
  if(entry && entry.teasers && entry.teasers.length > 0) {
    teasers = entry.teasers.map((e) => {
      return getTeaserEntry(e)
    }).filter(e => e != null)
  }

  return teasers
}

export function getRessortModuleEntry(entry: any, index: number) : RessortModuleEntry | undefined {
  //TODO Module
  let contentType =  getContentType(entry);
  if(entry?.elementsView == "list") {
    contentType = ContentType.ListModule;
  }

  if(entry?.withLeadStory === true) {
    contentType = ContentType.HighlightModule;
  }
  
  if(entry?.elementsView == "slider") {
    contentType = ContentType.StoryModule;
  }

  if(entry?.withLeadStory === false && entry.elementsView == "tile") {
    contentType = ContentType.ListModule;
  }

  if(entry && contentType != ContentType.Unknown) {
    let teasers = getTeaserEntries(entry)

    const featured = (teasers && teasers.length == 1 && entry.elementsView == "tile")    
    if(featured) {
      teasers = teasers.map((f) => {
        f.featured = true
        return f
      })
    }

    if(entry?.highlight && teasers && teasers[0]) {
      teasers[0].highlight = true;
    }

    let searchModuleOptions = [] as SearchModuleOption[]
    if(contentType == ContentType.SearchModule && entry.searchModuleOptions) {
      searchModuleOptions = entry.searchModuleOptions.filter((e) => e.values).map((e) => {
        return {
          label: e.label,
          param: e.param,
          type: e.type,
          values: e.values 
        }
      })
    }

    let ressortEntry: RessortModuleEntry = {
      id: entry.id || `${entry.type}-${index}`,
      type: contentType,
      title: entry.title ?? "",
      elementsViewType: entry.elementsView || '',
      contrastColor: entry.showContrastColor == true,
      loadMore: entry.showLoadMoreButton == true,
      teasers: teasers,
      featured: featured,
      searchModuleOptions: searchModuleOptions,
      frame: entry?.frame == "withFrame",
      icon: entry?.frameIcon ?? null,
      highlight: entry?.highlight === true,
      withLeadStory: entry?.withLeadStory === true,
      links: entry?.titleLinks ?? [],
      dataLinks: entry?.links ?? [],
      autoContentSize: entry?.autoContentSize ?? 0,
      linkList: entry?.linkList ?? null,
    }

    return ressortEntry
  }
  return undefined
}

export function getRessortEntry(entry: any) : RessortEntry {
  let ressortEntry: RessortEntry = {
    modules: [],
    breakingNews: [],
    status: 200,
    pageInformation: getPageInformation(entry),
    categories: []
  }

  if(entry && entry.modules) {
    let modules: RessortModuleEntry[] = []
    if(entry.modules && entry.modules.length > 0) {
      let index = 0
      modules = entry.modules.map((e) => {
       return getRessortModuleEntry(e, index++)
      })
    }

    ressortEntry.modules = modules.filter(m => m != null)
  }

  if(entry && entry.categories) {
    let categories: RessortCategoryEntry[] = []
    if(entry.categories && entry.categories.length > 0) {
      categories = entry.categories.map((c) => {
        return {
          externalId: c.externalId,
          name: c.name,
          entries: c.entries.map((e) => {
            return {
              externalId: e.externalId,
              name: e.name,
              entries: e.entries.map((e) => {
                return {
                  externalId: e.externalId,
                  name: e.name,
                  publicationMode: e.attributes.find((a) => a.key == "publicationmode")?.value || null,
                  confirmationListId: e.attributes.find((a) => a.key == "confirmationlistid")?.value || null,
                  newsletterId: e.attributes.find((a) => a.key == "newsletterid")?.value || null
                }
              }) || []
            } as RessortCategoryMainEntry
          }) || []
        } as RessortCategoryEntry
      }) || []
    }
    ressortEntry.categories = categories
  }

  if(entry && entry.pageInformation && entry.pageInformation.breakingNews) {
    let breakingNews: TeaserEntry[] = []
    if(entry.pageInformation.breakingNews.length > 0) {
      breakingNews = entry.pageInformation.breakingNews.map((e) => {
       return getTeaserEntry(e)
      }).filter(e => e != null)
    }

    ressortEntry.breakingNews = breakingNews.filter(m => m != null)
  }
  return ressortEntry
}

export function processPersonalResorts(result: RessortEntry | null, id: string, title: string) {
  if(process.env.NEXT_PUBLIC_APP_THEME == AppTheme.nb) {
    return processPersonalResortsNB(result, id, title);
  }
  else {
    return processPersonalResortsNNV1(result, id, title);
  }
}

function processPersonalResortsNB(result: RessortEntry | null, id: string, title: string) {
  let addedModules: RessortModuleEntry[] = []
  if (result && result.modules && result.modules.length > 0) {
    let modules: RessortModuleEntry[] = []
    result.modules.forEach((module) => {
      let teasers = module.teasers?.map((teaser) => {
        return { ...teaser, minimizedView: true }
      })
      modules.push({...module, teasers: teasers, featured: false, title: ""})
    })
    addedModules.push({
      id: id,
      type: ContentType.MyRessortHeadline,
      title: title,
      elementsViewType: "title",
      contrastColor: false,
      loadMore: false,
      teasers: [],
      featured: false,
      frame: false,
      links: []
    });
    Array.prototype.push.apply(addedModules, modules);
    addedModules.push({
      id: id,
      type: ContentType.MyRessortLoadRessort,
      title: title,
      elementsViewType: "title",
      contrastColor: false,
      loadMore: false,
      teasers: [],
      featured: false,
      frame: false,
      links: []
    });
  }
  return addedModules;
}

function processPersonalResortsNNV1(result: RessortEntry | null, id: string, title: string) {
  let addedModules: RessortModuleEntry[] = []
  if (result && result.modules && result.modules.length > 0) {
    let modules: RessortModuleEntry[] = []
    result.modules.forEach((module) => {
      let teasers = module.teasers?.map((teaser) => {
        return { ...teaser, minimizedView: true, highlight: false }
      })
      modules.push({...module, teasers: teasers, highlight: false, featured: false, title: "", type: ContentType.ListModule})
    })
    addedModules.push({
      id: id,
      type: ContentType.MyRessortHeadline,
      title: title,
      elementsViewType: "title",
      contrastColor: false,
      loadMore: false,
      teasers: [],
      featured: false,
      frame: false,
      links: []
    });
    Array.prototype.push.apply(addedModules, modules);
    addedModules.push({
      id: id,
      type: ContentType.MyRessortLoadRessort,
      title: title,
      elementsViewType: "title",
      contrastColor: false,
      loadMore: false,
      teasers: [],
      featured: false,
      frame: false,
      links: []
    });
  }
  return addedModules;
}

function processPersonalResortsNN(result: RessortEntry | null, id: string, title: string) {
  let addedModules: RessortModuleEntry[] = []
  let teasers = []  
  if (result && result.modules && result.modules.length > 0) {
    result.modules.forEach((module) => {
      let moduleTeasers = module.teasers?.map((teaser) => {
        return { ...teaser, minimizedView: true }
      })
      Array.prototype.push.apply(teasers, moduleTeasers);
    })          
  }
  if(teasers.length > 0) {
    addedModules.push({
      id: id+".t",
      teasers: teasers,
      frame: true,
      icon: 'heart',
      type: ContentType.ListModule,
      title:title,
      contrastColor: false,
      featured: false,
      loadMore: false,
      elementsViewType: "",
      links: []
    });
    addedModules.push({
      id: id,
      type: ContentType.MyRessortLoadRessort,
      title: title,
      elementsViewType: "title",
      contrastColor: false,
      loadMore: false,
      teasers: [],
      featured: false,
      frame: false,
      links: []
    });
  }
  return addedModules;
}